// Implements: F-RPT-005, F-RPT-006, F-RPT-007, F-RPT-009
/**
 * Mockup v3 P9: the client of an organization's cost (by model and by flow version), activity and
 * latency reports, `GET /reports/organizations/{org}/costs|activity|latency?from=&to=` (org admin
 * only), and of the export twins the screen offers next to them (owner 2026-10-19),
 * `…/{report}/export?from=&to=&format=csv`: the window on screen, as a CSV.
 *
 * The address names only the organization on screen, its id escaped; the server decides access
 * before it reads any id and answers a refused reader with the `404` of a path that does not
 * exist. A read whose body is missing is a broken answer, not an empty report. An export over the
 * server's row cap is a `422` that names no file; the download raises it as an `ApiError`.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpDownload, httpRequest } from "../../../shared/api/httpClient";
import type { Download } from "../../../shared/api/httpClient";
import { reportWindowQuery } from "../../../shared/api/reportWindow";
import type { ActivityReportView, CostReportView, LatencyReportView, ReportWindow } from "../../../types/reports";

function organizationReport(organizationId: string, report: "costs" | "activity" | "latency", window: ReportWindow): string {
  return `${API_BASE}/reports/organizations/${encodeURIComponent(organizationId)}/${report}?${reportWindowQuery(window).toString()}`;
}

function required<T>(body: T | undefined, path: string): T {
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}

export async function fetchOrgCosts(
  organizationId: string,
  window: ReportWindow,
  signal?: AbortSignal,
): Promise<CostReportView> {
  const path = organizationReport(organizationId, "costs", window);
  return required(await httpRequest<CostReportView>(path, { signal }), path);
}

export async function fetchOrgActivity(
  organizationId: string,
  window: ReportWindow,
  signal?: AbortSignal,
): Promise<ActivityReportView> {
  const path = organizationReport(organizationId, "activity", window);
  return required(await httpRequest<ActivityReportView>(path, { signal }), path);
}


export async function fetchOrgLatency(
  organizationId: string,
  window: ReportWindow,
  signal?: AbortSignal,
): Promise<LatencyReportView> {
  const path = organizationReport(organizationId, "latency", window);
  return required(await httpRequest<LatencyReportView>(path, { signal }), path);
}

/** The reports whose export the screen offers beside them, for the window they show. */
export type OrgWindowExport = "activity" | "latency";

/** The file name of an export whose answer names none: the server's own stem (`export_name`). */
export const ORG_EXPORT_NAMES: Readonly<Record<OrgWindowExport, string>> = {
  activity: "actividad-organizacion.csv",
  latency: "latencia-organizacion.csv",
};

export function orgReportExportPath(organizationId: string, report: OrgWindowExport, window: ReportWindow): string {
  const query = reportWindowQuery(window);
  query.set("format", "csv");
  return `${API_BASE}/reports/organizations/${encodeURIComponent(organizationId)}/${report}/export?${query.toString()}`;
}

export function downloadOrgReport(organizationId: string, report: OrgWindowExport, window: ReportWindow): Promise<Download> {
  return httpDownload(orgReportExportPath(organizationId, report, window));
}
